import { useState } from 'react';
import { Pencil } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { useSetDoctor } from './useAptCounter';
import { DoctorNameDialog } from './DoctorNameDialog';

interface Props {
  doctorIn: boolean;
  doctorName: string | null;
}

export function DoctorSwitch({ doctorIn, doctorName }: Props) {
  const { t } = useTranslation();
  const setDoctor = useSetDoctor();
  const [dialogOpen, setDialogOpen] = useState(false);
  // Tracks whether the dialog was opened by an OFF→ON toggle vs. an explicit
  // "edit" click while already on. The former cancels back to OFF on dismiss;
  // the latter just closes.
  const [openedByToggle, setOpenedByToggle] = useState(false);

  const handleToggle = () => {
    if (doctorIn) {
      // Flipping OFF — no prompt, just persist.
      setDoctor.mutate({ doctorIn: false });
    } else {
      setOpenedByToggle(true);
      setDialogOpen(true);
    }
  };

  const handleEditClick = () => {
    setOpenedByToggle(false);
    setDialogOpen(true);
  };

  const handleSubmit = (name: string) => {
    setDoctor.mutate(
      { doctorIn: true, doctorName: name },
      { onSuccess: () => setDialogOpen(false) },
    );
  };

  const handleCancel = () => {
    setDialogOpen(false);
    // If this dialog was the result of an off→on toggle and the user backed
    // out, leave the doctor OFF (no mutation was sent). If it was an explicit
    // edit while already in, also do nothing.
    setOpenedByToggle(false);
  };

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-line bg-paper p-4">
      <div className="flex items-center gap-3">
        <button
          type="button"
          role="switch"
          aria-checked={doctorIn}
          aria-label={t('aptCounter.doctorIn')}
          onClick={handleToggle}
          disabled={setDoctor.isPending}
          className={cn(
            'relative inline-flex h-7 w-12 shrink-0 cursor-pointer items-center rounded-full border border-line-2 transition-colors disabled:opacity-50',
            doctorIn ? 'bg-success' : 'bg-paper-2',
          )}
        >
          <span
            className={cn(
              'inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform',
              doctorIn ? 'translate-x-6' : 'translate-x-1',
            )}
          />
        </button>
        <div>
          <div className="text-[14px] font-semibold tracking-[-0.01em] text-ink">
            {doctorIn ? t('aptCounter.doctorIn') : t('aptCounter.doctorOut')}
          </div>
          {doctorIn && doctorName && (
            <div className="mt-0.5 flex items-center gap-1.5 text-[13px] text-muted">
              <span>{doctorName}</span>
              <button
                type="button"
                onClick={handleEditClick}
                aria-label={t('aptCounter.editDoctorName')}
                className="rounded p-0.5 text-muted hover:bg-paper-2 hover:text-ink"
              >
                <Pencil size={12} />
              </button>
            </div>
          )}
        </div>
      </div>

      <DoctorNameDialog
        open={dialogOpen}
        initialName={doctorName ?? ''}
        title={openedByToggle ? t('aptCounter.markDoctorIn') : t('aptCounter.updateDoctorName')}
        onCancel={handleCancel}
        onSubmit={handleSubmit}
        submitting={setDoctor.isPending}
      />
    </div>
  );
}
